import { resolveSpokenAgentName } from './agentNameMatch';
import {
  arcturianActionApi,
  type ArcturianActionReceipt,
  type ArcturianAuthorityGrant,
  type ArcturianAuthorityTargets,
  type ArcturianExecuteResponse,
} from '../api/client';

export const ARCTURIAN_ACTION_PROTOCOL = 'agentos.arcturian-action.v1';
export const ARCTURIAN_RESOLVER_V2 = 'agentos.arcturian-action.v2';
/** v3 ergänzt `query_status` — „was macht 3dApi" ist etwas anderes als
 *  „zeig mir 3dApi", und nur die Trennung macht den Nachschlag erzwingbar. */
export const ARCTURIAN_RESOLVER_V3 = 'agentos.arcturian-action.v3';

export type ArcturianDecision = 'none' | 'clarify' | 'action';
export type ArcturianActionKind =
  | 'send_internal_message'
  | 'delegate_internal'
  | 'create_collab'
  | 'start_workflow'
  | 'navigate_ui'
  | 'query_status';

export interface ArcturianResolverArguments {
  decision: ArcturianDecision;
  kind: ArcturianActionKind | null;
  target: string | null;
  target_kind: string | null;
  instruction: string | null;
}

export interface ArcturianActionClient {
  authorityTargets(): Promise<ArcturianAuthorityTargets>;
  grants(): Promise<ArcturianAuthorityGrant[]>;
  execute(body: {
    target: string;
    channel: 'direct_iacp';
    content: string;
    kind: string;
    artifacts: unknown[];
    response_contract: Record<string, unknown>;
    authority_ref: { grant_id: string; revision: number };
    conversation_id?: string;
  }, idempotencyKey: string): Promise<ArcturianExecuteResponse>;
}

export interface ResolverExecutionOptions {
  callId: string;
  rawArguments: string;
  resolverContract?: string;
  conversationId?: string;
  client?: ArcturianActionClient;
  navigate?: (target: string) => void;
  setFocusUpdates?: (enabled: boolean) => void;
  /** v3: Statusfrage erkannt. Der Aufrufer schiebt daraufhin den erzwungenen
   *  Nachschlag-Zug VOR den Sprechzug. Kein Vollmachtspfad — es ist ein Lesen. */
  onStatusQuery?: (target: string) => void;
  now?: () => number;
}

const EXECUTABLE_KINDS = new Set<ArcturianActionKind>([
  'send_internal_message',
  'delegate_internal',
  'create_collab',
  'start_workflow',
]);

const DIRECT_KINDS = new Set<ArcturianActionKind>([
  'send_internal_message',
  'delegate_internal',
]);

/**
 * Ein verworfener Resolver-Zug — mit dem Grund, nicht nur der Tatsache.
 *
 * Vorher warfen sieben verschiedene Ursachen denselben Text
 * `invalid_resolver_payload`. Für Alex sah das aus, als rede Arcturian Unsinn;
 * tatsächlich wurde sein Auftrag von UNSERER Prüfung verworfen, und niemand
 * konnte sagen warum — weder er, noch ich beim Nachlesen des Protokolls, noch
 * das Modell selbst, das denselben Fehler gleich nochmal machte
 * (Sitzung vs_CloudV2_531973, zweimal hintereinander).
 *
 * `code` wandert in die Transkriptzeile und damit ins Diagnose-Protokoll,
 * `detail` zusätzlich in die Werkzeug-Ausgabe — damit das MODELL beim nächsten
 * Zug weiß, was es anders machen muss, statt sich blind zu wiederholen.
 *
 * `detail` nennt bewusst nur Feldnamen und die verletzte Regel, niemals den
 * gesprochenen Inhalt: Der Grund gehört ins Protokoll, das Gesagte nicht.
 */
export class ResolverPayloadError extends Error {
  constructor(public readonly reason: string, public readonly detail?: string) {
    super(`invalid_resolver_payload_${reason}`);
    this.name = 'ResolverPayloadError';
  }
}

/**
 * Der Name ließ sich nicht auf einen Agenten abbilden — und zwar aus einem
 * NAMHAFTEN Grund.
 *
 * Bis heute trugen drei verschiedene Lagen dieselbe Meldung
 * `authority_missing_for_target`: Name unbekannt, Name mehrdeutig, Name klar
 * aber keine Vollmacht. Der Selbstdiagnose-Eintrag #1036 aus Alex' Test um
 * 17:36 sagte deshalb nur, DASS etwas fehlte — nicht was.
 *
 * `retryable` ist hier bewusst `true`, anders als bei einer kaputten Nutzlast:
 * Ein Name lässt sich durch Nachfragen klären, eine falsche Form nicht.
 */
export class TargetResolutionError extends Error {
  constructor(
    public readonly code: string,
    public readonly detail: string,
    public readonly retryable = true,
  ) {
    super(code);
    this.name = 'TargetResolutionError';
  }
}

export function parseArcturianResolverArguments(
  raw: string,
  requireTargetKind: boolean,
  allowQueryStatus = false,
): ArcturianResolverArguments {
  let value: unknown;
  try { value = JSON.parse(raw); } catch { throw new ResolverPayloadError('json', 'Antwort war kein gültiges JSON'); }
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new ResolverPayloadError('shape', 'Erwartet wird ein Objekt, kein Array und kein Skalar');
  const input = value as Record<string, unknown>;
  if (!['none', 'clarify', 'action'].includes(String(input.decision))) {
    throw new ResolverPayloadError('decision', `decision muss none|clarify|action sein, war: ${JSON.stringify(input.decision)}`);
  }
  const nullableString = (key: string): string | null => {
    const item = input[key];
    if (item == null) return null;
    if (typeof item !== 'string') {
      throw new ResolverPayloadError('field_type', `${key} muss ein String oder null sein, war: ${typeof item}`);
    }
    return item.trim() || null;
  };
  const kind = nullableString('kind');
  // Die erlaubte Menge hängt an der ausgehandelten Fassung: Wer v2 spricht,
  // darf `query_status` NICHT liefern — sonst nähme der Client eine Entscheidung
  // an, für die diese Sitzung keine Nachschlag-Vorlage hat.
  const allowedKinds: ArcturianActionKind[] = [
    'send_internal_message', 'delegate_internal', 'create_collab', 'start_workflow', 'navigate_ui',
    ...(allowQueryStatus ? ['query_status' as ArcturianActionKind] : []),
  ];
  if (kind != null && !allowedKinds.includes(kind as ArcturianActionKind)) {
    throw new ResolverPayloadError('unknown_kind', `kind "${kind}" ist nicht erlaubt. Erlaubt: ${allowedKinds.join(', ')}`);
  }
  // ANWESENHEITSPFLICHT FUER `target_kind` ENTFAELLT — bewusst, mit Beleg.
  //
  // Sie sollte erkennen, dass die Gegenseite wirklich die ausgehandelte Fassung
  // spricht. Das leistet inzwischen die Fassung selbst: `arcturian_resolver`
  // steht im Mint und wird gegen eine Aufzaehlung geprueft. Die Anwesenheit
  // pruefte also ein zweites Mal, was schon feststand — und verwarf dafuer
  // echte Auftraege.
  //
  // Zwei Messungen an einem Abend haben das entschieden:
  //   * #1040, Alex' erste v3-Sitzung: v3 fuehrte `target_kind` in `required`,
  //     definierte es aber nicht in `properties`. Ein Pflichtfeld, das es im
  //     Schema nicht gibt, KANN das Modell nicht setzen (AiApi, 8/8 null).
  //   * navigate_ui lieferte das Feld nur in 2 von 6 Laeufen (AiApi, P0-3).
  //
  // Der Wert bleibt geprueft, wo er etwas entscheidet — siehe unten. Nur seine
  // blosse Anwesenheit taugt nicht als Vertragsbeweis.
  void requireTargetKind;
  return {
    decision: input.decision as ArcturianDecision,
    kind: kind as ArcturianActionKind | null,
    target: nullableString('target'),
    target_kind: nullableString('target_kind'),
    instruction: nullableString('instruction'),
  };
}

function eligibleGrants(
  grants: readonly ArcturianAuthorityGrant[],
  projection: ArcturianAuthorityTargets,
  now: number,
): ArcturianAuthorityGrant[] {
  return grants.filter((grant) =>
    grant.principal_user_id === projection.principal_user_id
    && grant.tenant_id === projection.tenant_id
    && grant.revoked_at == null
    && (grant.expires_at == null || grant.expires_at > now)
    && grant.scopes.includes('internal_message')
    && grant.modes.includes('direct_iacp')
    && !grant.targets.includes('*'));
}

function validateReceipt(
  response: ArcturianExecuteResponse,
  request: { target: string; content: string; grant: ArcturianAuthorityGrant },
): ArcturianActionReceipt {
  const { action, receipt } = response;
  const sameAuthority = receipt.authority_ref?.grant_id === request.grant.grant_id
    && receipt.authority_ref?.revision === request.grant.revision;
  const valid = action?.protocol === ARCTURIAN_ACTION_PROTOCOL
    && receipt?.protocol === ARCTURIAN_ACTION_PROTOCOL
    && action.action_id === receipt.action_id
    && action.task_id === receipt.task_id
    && action.conversation_id === receipt.conversation_id
    && action.message_id === receipt.message_id
    && action.principal_user_id === receipt.principal_user_id
    && action.tenant_id === receipt.tenant_id
    && action.revision === receipt.revision
    && action.state === receipt.state
    && action.delivered_at === receipt.delivered_at
    && action.target_session === request.target
    && action.content === request.content
    && action.channel === 'direct_iacp'
    && sameAuthority
    && Boolean(receipt.action_id && receipt.conversation_id && receipt.message_id)
    && Number.isInteger(receipt.revision) && receipt.revision >= 1
    && receipt.delivered_at != null
    && !['understood', 'authorized'].includes(receipt.state);
  if (!valid) throw new Error('invalid_action_receipt');
  return receipt;
}

/** Execute one server-resolved Realtime call and return the exact
 * function_call_output string expected by the primary-audio follow-up. */
export async function executeArcturianResolver(options: ResolverExecutionOptions): Promise<string> {
  const callId = options.callId.trim();
  if (!callId || callId.length > 300) throw new Error('invalid_resolver_call_id');
  const isV3 = options.resolverContract === ARCTURIAN_RESOLVER_V3;
  const requireTargetKind = options.resolverContract === ARCTURIAN_RESOLVER_V2 || isV3;
  const args = parseArcturianResolverArguments(options.rawArguments, requireTargetKind, isV3);
  if (args.decision === 'none' || args.decision === 'clarify') return '{}';
  if (!args.kind || !args.target) throw new Error('invalid_resolver_payload');

  // Resolver v2 deliberately leaves target_kind open for client-owned local
  // actions. `focus_updates` is a session-local state switch, not an authority
  // target and never a cloud-api action. Handle it before any projection/grant
  // read so enabling updates cannot accidentally depend on federation state.
  // Fehlt `target_kind` bei einer Navigation, wird es aus dem Ziel ABGELEITET —
  // und das ist kein Raten, sondern eine Entscheidung ueber einem Wertevorrat
  // von zwei: „on"/„off" schaltet die Aktualisierungen, alles andere ist ein
  // Agentenname. Ein Agent kann nicht „on" heissen; die Zielliste entscheidet
  // darueber ohnehin an der naechsten Stelle.
  //
  // Ohne diese Ableitung verwirft der Client laut AiApis Messung vier von sechs
  // Navigationen (2/6 lieferten das Feld) — an einem Feld, dessen Wert sich aus
  // dem Ziel eindeutig ergibt.
  //
  // Dieselbe Ableitung greift bei einem UNBEKANNTEN Wert, nicht nur bei einem
  // fehlenden — sonst waere die Regel verkehrt herum: `tk=None` ginge durch,
  // `tk='ui_section'` scheiterte, obwohl der erfundene Wert WENIGER traegt als
  // gar keiner. AiApi mass 4 von 32 solcher Erfindungen (`focus_view`,
  // `ui_section`, `interface_section`), gebuendelt in einem Lauf, nicht
  // reproduzierbar; eine Aufzaehlung im Feld machte es schlechter (7/8), und
  // `strict: true` weist Realtime ganz ab.
  //
  // Es gilt also das Vokabular, das ICH kenne: alles andere zaehlt als „nicht
  // geaeussert" und wird aus dem Ziel abgeleitet.
  const namedTargetKind = args.target_kind === 'focus_agent' || args.target_kind === 'focus_updates'
    ? args.target_kind
    : null;
  const targetKind = namedTargetKind
    ?? (args.kind === 'navigate_ui'
      ? (['on', 'off'].includes((args.target || '').toLowerCase()) ? 'focus_updates' : 'focus_agent')
      : null);

  if (args.kind === 'navigate_ui' && targetKind === 'focus_updates') {
    if (!['on', 'off'].includes(args.target) || !options.setFocusUpdates) {
      throw new Error('unsupported_ui_navigation');
    }
    options.setFocusUpdates(args.target === 'on');
    return '{}';
  }

  // Vor jeder Projektion: Ein Nachschlag ist ein LESEN. Liefe er über die
  // Vollmachtsprojektion, würde eine fehlende Vollmacht eine bloße FRAGE mit
  // `authority_missing_for_target` beantworten — und wieder wie ein kaputter
  // Agent aussehen. Den Namen löst die Gegenseite auf; hier wird er nur
  // durchgereicht, damit es genau eine Stelle gibt, die falten kann.
  if (args.kind === 'query_status') {
    if (!options.onStatusQuery) {
      throw new TargetResolutionError(
        'status_lookup_unavailable',
        'Diese Sitzung führt kein Nachschlag-Werkzeug. Sag dem Bediener, dass du '
        + 'den Status gerade nicht abfragen kannst — erfinde keinen.',
        false,
      );
    }
    options.onStatusQuery(args.target);
    return '{}';
  }

  const client = options.client || arcturianActionApi;
  // This projection is both the fresh owner-filtered target set and the call
  // that establishes cloud-api's audited owner-implicit baseline grant.
  const projection = await client.authorityTargets();
  if (projection.protocol !== ARCTURIAN_ACTION_PROTOCOL
      || projection.scope !== 'internal_message'
      || projection.mode !== 'direct_iacp'
      || !Array.isArray(projection.targets)
      || projection.targets.some((target) => !target || target.trim() !== target || target === '*')
      || new Set(projection.targets).size !== projection.targets.length) {
    throw new Error('invalid_authority_projection');
  }
  // Gesprochene Namen kommen als „drei D API" oder „Cloud V zwei" an, während
  // die Registrierung `3dApi` und `CloudV2` führt (#1037). Die Ähnlichkeits-
  // suche ist deterministisch, ohne Modell — und sie darf bei Zweifel NICHT
  // wählen: ein stiller Fehlgriff schickt die Nachricht an den falschen
  // Agenten, und der falsche Agent antwortet brav.
  const resolution = resolveSpokenAgentName(args.target, projection.targets);
  if (resolution.decision === 'none') {
    throw new TargetResolutionError(
      'unknown_target_name',
      `Kein registrierter Agent passt zu dem genannten Namen (${projection.targets.length} geprüft). `
      + 'Frag den Bediener nach der Schreibweise oder nenne ihm ähnliche Namen.',
    );
  }
  if (resolution.decision === 'ambiguous') {
    throw new TargetResolutionError(
      'ambiguous_target_name',
      `Mehrere Agenten passen gleich gut: ${resolution.candidates.map((c) => c.name).join(', ')}. `
      + 'Frag zurück, welcher gemeint ist — nicht raten.',
    );
  }
  const target = resolution.match.name;

  if (args.kind === 'navigate_ui') {
    if (targetKind !== 'focus_agent' || !options.navigate) {
      throw new Error('unsupported_ui_navigation');
    }
    options.navigate(target);
    return '{}';
  }
  // The web executor currently exposes the same frozen direct-IACP slice as
  // iOS. Collaboration/workflow remain valid resolver kinds but need their own
  // channel-specific UI and route contracts before execution.
  if (!EXECUTABLE_KINDS.has(args.kind) || !DIRECT_KINDS.has(args.kind) || !args.instruction) {
    throw new Error('unsupported_action_kind');
  }

  const grants = eligibleGrants(await client.grants(), projection, (options.now || Date.now)() / 1000);
  const grant = grants
    .filter((candidate) => candidate.targets.includes(target))
    .sort((a, b) => b.updated_at - a.updated_at || b.grant_id.localeCompare(a.grant_id))[0];
  if (!grant) {
    throw new TargetResolutionError(
      'authority_missing_for_target',
      `Der Agent ${target} ist bekannt, aber keine gültige Vollmacht deckt ihn ab. `
      + 'Das ist keine Namensfrage — der Bediener muss die Vollmacht erteilen.',
      false,
    );
  }

  const response = await client.execute({
    target,
    channel: 'direct_iacp',
    content: args.instruction,
    kind: args.kind,
    artifacts: [],
    response_contract: {},
    authority_ref: { grant_id: grant.grant_id, revision: grant.revision },
    ...(options.conversationId ? { conversation_id: options.conversationId } : {}),
  }, callId);
  return JSON.stringify(validateReceipt(response, { target, content: args.instruction, grant }));
}

export function resolverErrorOutput(error: unknown): string {
  const code = error instanceof Error && /^[a-z0-9_]+$/.test(error.message)
    ? error.message
    : 'action_rejected';
  // `detail` sagt dem Modell, WAS es anders machen muss. Ohne das wiederholt es
  // denselben Fehler — belegt in Alex' Sitzung vs_CloudV2_531973, wo derselbe
  // Zug zweimal hintereinander an derselben Regel scheiterte.
  if (error instanceof TargetResolutionError) {
    // Namensfragen sind wiederholbar: eine Rueckfrage kann sie klaeren. Eine
    // kaputte Nutzlast kann das nicht, deshalb bleibt die bei retryable:false.
    return JSON.stringify({ error: code, detail: error.detail, retryable: error.retryable });
  }
  const detail = error instanceof ResolverPayloadError ? error.detail : undefined;
  return JSON.stringify(detail ? { error: code, detail, retryable: false } : { error: code, retryable: false });
}
